import React from "react";

import { MdArrowBack } from "@react-icons/all-files/md/MdArrowBack";
import { MdFilterList } from "@react-icons/all-files/md/MdFilterList";
import { MdSearch } from "@react-icons/all-files/md/MdSearch";
import classNames from "classnames";
import currency from "currency.js";
import { useTranslation } from "react-i18next";
import { Link, LinkProps } from "react-router-dom-v5-compat";

import Icon from "Atoms/Icons";

import type { Dietary as DietaryType } from "Helpers/DataDefinitions/Dietaries";
import type { ResourceState } from "Helpers/DataDefinitions/ResourceStates";

import { useBrand } from "./BrandContext";
import { LoadingSpinner } from "./Loading";
import { useVenue } from "./VenueContext";
import { PriceUnit } from "./schema";

export const TitleBar = ({
  children,
  alwaysShowBackButton,
}: {
  children: React.ReactNode;
  alwaysShowBackButton?: boolean;
}) => {
  return (
    <div className="-mx-16 md:mx-0 py-8 px-8 z-40 flex items-center justify-between sticky  md:static top-0 inset-x-0 bg-venue-secondary text-2xl font-medium ">
      <div
        className={classNames("w-32", {
          "md:hidden": !alwaysShowBackButton,
        })}
      >
        <Link to=".." className="text-xl">
          <MdArrowBack />
        </Link>
      </div>
      <div className="flex-1 text-center ">{children}</div>
      <div
        className={classNames("w-32", {
          "md:hidden": !alwaysShowBackButton,
        })}
      ></div>
    </div>
  );
};

export const ResponsiveTitleBar = ({
  children,
  alwaysShowBackButton,
}: {
  children: React.ReactNode;
  alwaysShowBackButton?: boolean;
}) => {
  const { t } = useTranslation();
  return (
    <div>
      <div className="sticky z-10  top-0 md:hidden">
        <TitleBar>
          <div className="md:text-4xl md:font-bold">{children}</div>
        </TitleBar>
      </div>
      <div className="hidden md:block ">
        <TitleBar>
          <div className="md:text-4xl md:font-bold">{children}</div>
        </TitleBar>
      </div>
    </div>
  );
};

interface SearchFieldProps {
  searchTerm?: string;
  onChange?: (searchTerm: string) => void;
}

export const ErrorBox = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="text-center shadow-venue-brand-style p-12 border-2 border-venue-highlight text-venue-highlight bg-venue-tertiary rounded-venue-brand">
      {children}
    </div>
  );
};
export const PageContent = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  return <div className={`p-16 ${className} `}>{children}</div>;
};
export const SearchField = ({ searchTerm, onChange }: SearchFieldProps) => {
  return (
    <div className="relative bg-venue-tertiary p-8 rounded-venue-brand flex items-center">
      <MdSearch className="text-xl flex-none" />
      <input
        type="text"
        placeholder="Search"
        className="w-full px-8 outline-none bg-venue-tertiary"
        value={searchTerm}
        onChange={(e) => onChange && onChange(e.target.value)}
      />
      <MdFilterList className="text-xl flex-none" />
    </div>
  );
};

export { useSubmitButton } from "Atoms/Buttons/SubmitButton";
interface ButtonOptions {
  primary?: boolean;
  secondary?: boolean;
  alert?: boolean;
  children: React.ReactNode;
}
type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> &
  ButtonOptions & {
    tag?: "button";
    submissionState?: ResourceState;
    submissionOptions?: {
      loadingMessage?: string;
      errorMessage?: string;
    };
  };
type LinkButtonProps = ButtonOptions & { to: string };

export const Button = ({
  primary,
  secondary,
  alert,
  children,
  submissionState,
  submissionOptions,
  ...props
}: ButtonProps) => {
  const Tag = props.tag || "button";

  return (
    <Tag
      className={classNames(
        "block  text-center w-full justify-center items-center p-12 cursor-pointer disabled:cursor-not-allowed rounded-venue-brand font-bold text-lg",
        {
          "bg-venue-primary text-venue-secondary": primary,
          "border-venue-primary border-2 bg-venue-tertiary": secondary,
          "bg-venue-highlight text-venue-secondary": alert,
        }
      )}
      {...props}
    >
      <ButtonContent
        submissionState={submissionState || "LOADED"}
        submissionOptions={submissionOptions}
      >
        {children}
      </ButtonContent>
    </Tag>
  );
};

const priceUnitsPerSymbols = {
  item: "/item",
  kilogram: "/Kg",
  "100 gram": "/100g",
  gram: "/g",
  litre: "/L",
  millilitre: "/mL",
  open: "",
};
const countDecimals = function (value: number) {
  if (Math.floor(value) === value) return 0;
  return value.toString().split(".")[1].length || 0;
};

export const Price = ({
  value,
  priceUnit,
  showCurrency,
  showDecimals,
}: {
  value: number | string;
  priceUnit?: PriceUnit;
  showCurrency?: boolean;
  showDecimals?: boolean;
}) => {
  const parsedNumber = typeof value == "string" ? parseFloat(value) : value;
  const safeNumber = isNaN(parsedNumber) ? 0 : parsedNumber;
  const { currencyDisplayType } = useBrand();
  const { t } = useTranslation();

  const hasSymbol =
    showCurrency ||
    currencyDisplayType == "terse_with_symbol" ||
    currencyDisplayType == "normal_with_symbol";
  const isTerse =
    !showDecimals &&
    (currencyDisplayType == "terse_with_symbol" ||
      currencyDisplayType == "terse");

  const currencyOptions: currency.Options = {
    symbol: hasSymbol ? t("currencySymbol") : "",
    precision: isTerse ? countDecimals(safeNumber) : 2,
  };
  const hasPriceUnit = priceUnit && priceUnit != "item";
  const price = currency(safeNumber, currencyOptions).format();
  return (
    <>
      {price}
      {hasPriceUnit && priceUnitsPerSymbols[priceUnit]}
    </>
  );
};

const ButtonContent = ({
  children,
  submissionState,
  submissionOptions,
}: {
  submissionState: ResourceState;
  submissionOptions?: {
    loadingMessage?: string;
    errorMessage?: string;
  };
  children: React.ReactNode;
}): JSX.Element => {
  const { t } = useTranslation();

  switch (submissionState) {
    case "LOADED":
      return <>{children}</>;
    case "LOADING":
    case "UNLOADED":
      return (
        <>
          {submissionOptions?.loadingMessage || t("buttons.saving")}{" "}
          <LoadingSpinner
            icon="loading"
            color={undefined}
            size="xs"
            className="animate-spin align-middle inline-block"
          />
        </>
      );
    case "ERROR":
    case "DIRTY":
      return (
        <>
          {submissionOptions?.errorMessage || (
            <>
              {t("buttons.savingFailed")}{" "}
              <Icon
                className={undefined}
                color={undefined}
                size="base"
                icon="error"
              />
            </>
          )}
        </>
      );
  }
};
export const LinkButton = ({
  to,
  primary,
  secondary,
  children,
  ...props
}: LinkButtonProps) => {
  return (
    <Link
      to={to}
      className={classNames(
        "block w-full text-center justify-center items-center p-12 cursor-pointer rounded-venue-brand font-bold text-lg max-w-screen-sm mx-auto",
        {
          "bg-venue-primary text-venue-secondary": primary,
          "border-venue-primary border-2": secondary,
        }
      )}
      {...props}
    >
      {children}
    </Link>
  );
};

export const ResponsivePrimaryAction = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  return (
    <>
      <div className="hidden md:block flex-1">{children}</div>
      <PrimaryAction>{children}</PrimaryAction>
    </>
  );
};
export const PrimaryAction = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="bg-venue-secondary fixed  px-16 bottom-0 py-8 inset-x-0 md:hidden">
      {children}
    </div>
  );
};

export const Dietary = ({ dietary }: { dietary: DietaryType }) => {
  const Icon = dietary.Icon;
  const { t } = useTranslation();
  return (
    <div className="bg-venue-tertiary rounded-full py-4 px-8 flex items-center space-x-8">
      <Icon />
      <div className="text-venue-primary text-lg font-medium">
        {t(`dietaries.${dietary.value}`)}
      </div>
    </div>
  );
};

export const DevNote = ({ note }: { note: string }) => {
  return <div className="italic text-xs"> 👨‍💻 - {note} </div>;
};
